import { Button } from 'antd';
import { FULL_ROUTES } from '@sonar-software/sonar-ts-constants';
import { getRandomString } from '@sonar-software/sonar-ts-lib';
import { sonarHttpGet } from '@sonar-software/sonar-http';

import { OidcProviderType, OidcSubdomainConfiguration } from '@sonar-software/sonar-security';
import React, { useCallback, useState } from 'react';

async function getAuthorizationEndpoint(oidcDomain: string): Promise<string> {
	const result = await sonarHttpGet<void, { authorization_endpoint: string }>({
		skipSonarHeaders: true,
		targetUri: `${oidcDomain}/.well-known/openid-configuration`,
		uriType: 'absolute'
	});

	return result.data?.authorization_endpoint ?? '';
}

function getFullOidcRedirect(options: { oidcClientID: string; url: string; }): string {
	const { origin } = window.location;
	const { oidcClientID, url } = options;
	const nonce = getRandomString(10);
	const state = getRandomString(10);

	return `${url}
			?client_id=${oidcClientID}
			&nonce=${nonce}
			&redirect_uri=${`${origin}${FULL_ROUTES.INTEGRATIONS_OIDC}`}
			&response_type=id_token
			&response_mode=fragment
			&scope=email openid profile
			&state=${state}`;
}

const OidcProviderNames: Record<OidcProviderType, string> = {
	azure: 'Azure',
	google: 'Google',
	none: 'None',
	okta: 'Okta',
	onelogin: 'OneLogin'
};

export interface OidcProviderLoginButtonProps {
	config: OidcSubdomainConfiguration;
}

export const OidcProviderLoginButton: React.FC<OidcProviderLoginButtonProps> = ({ config }) => {
	const [ isProcessing, setIsProcessing ] = useState(false);

	const handleClick = useCallback(async() => {
		setIsProcessing(true);
		const endpoint = await getAuthorizationEndpoint(config.oidcDomain);

		const redirectTarget = getFullOidcRedirect({
			oidcClientID: config.oidcClientID,
			url: endpoint
		});

		window.location.replace(redirectTarget);
	}, [ config.oidcClientID, config.oidcDomain ]);

	return (
		<div id='oidc-provider'>
			<Button
				loading={ isProcessing }
				onClick={ handleClick }
				type='primary'>
				{ OidcProviderNames[ config.provider ] }
			</Button>
		</div>
	);
};
